/**
 * sl-automation-refactor.4: AutomationConfigApiService
 *
 * API Client for Automation Config/Schedule management.
 * Communicates with Backend /api/workspace/:workspaceId/workflow/configs/* endpoints.
 *
 * SOLID:
 * - S: Single Responsibility - Only handles config API calls
 * - I: Interface Segregation - Focused API surface
 */

import { STEP_TYPES } from '@streamlain/field-types';
import { LiveData, Service } from '@toeverything/infra';

import { defaultWorkspaceApiClient } from '../../cloud';
// sl-trigger-removal.5: Import from types instead of removed trigger service
import type { CronPreset, TriggerType } from '../types';

// =============================================================================
// Types (matching Backend DTOs)
// =============================================================================

export interface ConfigResponse {
  id: string;
  workspaceId: string;
  docId: string;
  blockId: string;
  webhookUrl: string;
  status: 'active' | 'trashed';
  trashedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  permissionStatus: string;
  permissionError: string | null;
  schedule: ScheduleResponse | null;
}

export interface ScheduleResponse {
  id: string;
  configId: string;
  triggerType: TriggerType;
  cronPreset: CronPreset | null;
  timezone: string;
  enabled: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConfigInput {
  docId: string;
}

/**
 * ScheduleInput - Schedule configuration.
 *
 * The request persists schedule state. Y.js mirrors the same value for
 * local-first rendering.
 */
export interface ScheduleInput {
  triggerType: TriggerType;
  cronPreset?: CronPreset;
  timezone?: string;
  enabled?: boolean;
}

/**
 * E029-PREFLIGHT: Config data from verify-sync endpoint.
 * Contains all execution-relevant attributes for field-by-field comparison.
 */
export interface VerifySyncConfig {
  // Strings
  workflowId: string | null;
  sourceTableId: string | null;
  sourceDocId: string | null;
  // Deep objects
  outputTables: Array<{
    tableId: string;
    docId: string;
    mode?: string;
    matchConfigId?: string;
  }>;
  statusTracking: {
    enabled: boolean;
    mode?: string;
    columnId?: string;
    successValue: string;
  } | null;
  triggerConfig: {
    type?: string;
    enabled?: boolean;
    cronPreset?: string;
  } | null;
  batchConfig: {
    batchSize?: number;
    executionMode?: 'row' | 'batch';
  } | null;
  // Full objects (direct deep compare — no hashing)
  filterConfig: unknown | null;
  inputMappings: Array<{
    schemaFieldId: string;
    columnId: string | null;
    inputTableId?: string;
  }> | null;
  inputTables: Array<{
    id: string;
    tableId: string;
    tableName: string;
    docId?: string;
    role: 'lead' | 'secondary';
    filterGroup?: unknown;
    linkedVia?: string;
    cardinality?: 'one' | 'many';
    multiFieldName?: string;
  }> | null;
  outputMappings: unknown[] | null;
  matchConfigs: unknown[] | null;
  // Counts
  outputTablesCount: number;
  outputMappingsCount: number;
  selectedColumnsCount: number;
  inputMappingsCount: number;
  inputTablesCount: number;
  // Arrays
  selectedColumns: string[];
}

/**
 * E029-PREFLIGHT: Response from verify-sync endpoint.
 * Frontend compares this with local block props before execution.
 */
export interface VerifySyncResponse {
  verified: boolean;
  config: VerifySyncConfig | null;
  error?: string;
  errorKind?: VerifySyncErrorKind;
  status?: number;
}

export type VerifySyncErrorKind =
  | 'backend-unavailable'
  | 'permission-denied'
  | 'sync-not-ready';

const BACKEND_UNAVAILABLE_STATUSES = new Set([0, 500, 502, 503, 504]);

export function classifyVerifySyncFailure(
  status: number,
  error: string
): VerifySyncErrorKind {
  const normalized = error.toLowerCase();

  if (
    BACKEND_UNAVAILABLE_STATUSES.has(status) ||
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('load failed') ||
    normalized.includes('error occurred while trying to proxy') ||
    normalized.includes('backend unreachable')
  ) {
    return 'backend-unavailable';
  }

  if (status === 401 || status === 403) {
    return 'permission-denied';
  }

  return 'sync-not-ready';
}

export function isVerifySyncBackendUnavailable(
  response: VerifySyncResponse
): boolean {
  return response.errorKind === 'backend-unavailable';
}

export type WorkflowTestRunStatus = 'active' | 'expired' | 'superseded';
export type WorkflowTestStepResultStatus =
  | 'pending'
  | 'success'
  | 'failed'
  | 'suspended'
  | 'skipped';

export interface WorkflowTestRunData {
  id: string;
  workspaceId: string;
  automationDocId: string;
  automationBlockId: string;
  workflowId: string;
  sourceDocIds: string[];
  sourceTableId?: string | null;
  createdBy: string;
  automationConfigId?: string | null;
  workflowSnapshotVersion?: number | null;
  workflowSnapshotHash?: string | null;
  status: WorkflowTestRunStatus;
  inputPayload: unknown;
  sourceSummary?: unknown;
  validationSummary?: unknown;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

export interface WorkflowTestStepResultData {
  id: string;
  testRunId: string;
  workspaceId: string;
  workflowId: string;
  stepId: string;
  status: WorkflowTestStepResultStatus;
  inputData?: unknown;
  outputData?: unknown;
  error?: string | null;
  durationMs?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowTestRunResponse {
  run: WorkflowTestRunData;
  stepResults: WorkflowTestStepResultData[];
}

export interface WorkflowStepPreviewResponse {
  run: WorkflowTestRunData;
  stepResults: WorkflowTestStepResultData[];
  previewedStep?: WorkflowTestStepResultData | null;
}

export function extractStreamlainApiErrorMessage(
  responseText: string,
  fallback: string
): string {
  if (!responseText) return fallback;

  try {
    const body = JSON.parse(responseText) as {
      message?: string | string[];
      error?: string;
    };
    if (Array.isArray(body.message) && body.message.length > 0) {
      return body.message.join('\n');
    }
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message;
    }
    if (typeof body.error === 'string' && body.error.trim()) {
      return body.error;
    }
  } catch {
    // Plain-text backend responses are already user-facing enough to show.
  }

  return responseText;
}

async function readStreamlainApiError(
  response: Response,
  fallback: string
): Promise<string> {
  const text = await response.text();
  return extractStreamlainApiErrorMessage(text, fallback);
}

// =============================================================================
// Service
// =============================================================================

export class AutomationConfigApiService extends Service {
  /**
   * Loading state
   */
  readonly isLoading$ = new LiveData<boolean>(false);

  /**
   * Error state
   */
  readonly error$ = new LiveData<string | null>(null);

  private request(
    workspaceId: string,
    input: string | URL,
    init: RequestInit = {}
  ): Promise<Response> {
    const path = input.toString();
    const method = init.method ?? 'GET';
    const isMutation = method !== 'GET';
    return defaultWorkspaceApiClient.request({
      workspaceId,
      path,
      init,
      category: 'workspace-api',
      key: `${method}:${path}`,
      mode: isMutation ? 'foreground' : 'background',
      priority: isMutation ? 'foreground' : 'visible',
    });
  }

  // ===========================================================================
  // Config CRUD
  // ===========================================================================

  /**
   * Get config for a block
   */
  async getConfig(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<ConfigResponse | null> {
    try {
      const params = new URLSearchParams({ docId });
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}?${params}`
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to get config: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[ConfigApi] getConfig error:', error);
      return null;
    }
  }

  /**
   * Create or update config for a block
   */
  async saveConfig(
    blockId: string,
    workspaceId: string,
    input: ConfigInput
  ): Promise<ConfigResponse | null> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `Failed to save config: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('[ConfigApi] saveConfig error:', message);
      this.error$.next(message);
      return null;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Delete config for a block (CASCADE deletes schedule too)
   */
  async deleteConfig(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<boolean> {
    try {
      const params = new URLSearchParams({ docId });
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}?${params}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete config: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && typeof window !== 'undefined') {
        // Lets the Workflows page invalidate its "Used in N automations" pill
        // immediately instead of waiting for a refocus/reload.
        window.dispatchEvent(
          new CustomEvent('automation:config-deleted', {
            detail: { blockId, workspaceId, docId },
          })
        );
      }
      return result.success;
    } catch (error) {
      console.error('[ConfigApi] deleteConfig error:', error);
      return false;
    }
  }

  // ===========================================================================
  // Schedule CRUD
  // ===========================================================================

  /**
   * Create or update schedule for a config
   */
  async saveSchedule(
    blockId: string,
    workspaceId: string,
    docId: string,
    input: ScheduleInput
  ): Promise<ScheduleResponse | null> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const params = new URLSearchParams({ docId });
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/schedule?${params}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `Failed to save schedule: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('[ConfigApi] saveSchedule error:', message);
      this.error$.next(message);
      return null;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Delete schedule for a config (config remains)
   */
  async deleteSchedule(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<boolean> {
    try {
      const params = new URLSearchParams({ docId });
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/schedule?${params}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete schedule: ${response.status}`);
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('[ConfigApi] deleteSchedule error:', error);
      return false;
    }
  }

  // ===========================================================================
  // E029-PREFLIGHT: Pre-Flight Sync Verification
  // ===========================================================================

  /**
   * E029-PREFLIGHT: Verify that the backend can read the block's Y.js props.
   *
   * Returns the config as the backend sees it, so the frontend can compare
   * with its local state before triggering execution.
   */
  async verifySyncConfig(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<VerifySyncResponse> {
    let response: Response;
    try {
      response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/verify-sync`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ docId }),
        }
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Verify request failed';
      return {
        verified: false,
        config: null,
        error: message,
        errorKind: classifyVerifySyncFailure(0, message),
        status: 0,
      };
    }

    if (!response.ok) {
      const text = await response.text();
      const error = text || `verify-sync failed: ${response.status}`;
      return {
        verified: false,
        config: null,
        error,
        errorKind: classifyVerifySyncFailure(response.status, error),
        status: response.status,
      };
    }

    return await response.json();
  }

  // ===========================================================================
  // Workflow Test Runs
  // ===========================================================================

  async createWorkflowTestRun(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<WorkflowTestRunResponse> {
    const params = new URLSearchParams({ docId });
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/test-runs?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!response.ok) {
      throw new Error(
        await readStreamlainApiError(
          response,
          `create test run failed: ${response.status}`
        )
      );
    }

    return await response.json();
  }

  async createAdHocWorkflowTestRun(
    workspaceId: string,
    workflowId: string,
    inputData: Record<string, unknown>
  ): Promise<WorkflowTestRunResponse> {
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/test-runs/ad-hoc`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflowId, inputData }),
      }
    );

    if (!response.ok) {
      throw new Error(
        await readStreamlainApiError(
          response,
          `create ad-hoc test run failed: ${response.status}`
        )
      );
    }

    return await response.json();
  }

  async getLatestWorkflowTestRun(
    blockId: string,
    workspaceId: string,
    workflowId?: string | null
  ): Promise<WorkflowTestRunResponse | null> {
    const params = new URLSearchParams();
    if (workflowId) params.set('workflowId', workflowId);
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/test-runs/latest?${params}`
    );

    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(
        await readStreamlainApiError(
          response,
          `latest test run failed: ${response.status}`
        )
      );
    }

    return await response.json();
  }

  async previewWorkflowStep(
    blockId: string,
    workspaceId: string,
    docId: string,
    testRunId: string,
    stepId: string
  ): Promise<WorkflowStepPreviewResponse> {
    const params = new URLSearchParams({ docId });
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/test-runs/${testRunId}/preview-step?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stepId, allowedStepTypes: STEP_TYPES }),
      }
    );

    if (!response.ok) {
      throw new Error(
        await readStreamlainApiError(
          response,
          `step preview failed: ${response.status}`
        )
      );
    }

    return await response.json();
  }

  async previewAdHocWorkflowStep(
    workspaceId: string,
    workflowId: string,
    testRunId: string,
    stepId: string
  ): Promise<WorkflowStepPreviewResponse> {
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/test-runs/${testRunId}/preview-step`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workflowId,
          stepId,
          allowedStepTypes: STEP_TYPES,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        await readStreamlainApiError(
          response,
          `ad-hoc step preview failed: ${response.status}`
        )
      );
    }

    return await response.json();
  }

  // ===========================================================================
  // sl-kpg5.27: AI Key Availability Check
  // ===========================================================================

  /**
   * sl-kpg5.27: Check if AI providers used by the workflow have API keys configured.
   * Returns which providers are used and which are missing keys.
   */
  async checkAiKeys(
    blockId: string,
    workspaceId: string
  ): Promise<{ usesAi: boolean; providers: string[]; missingKeys: string[] }> {
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/ai-key-check`
    );
    if (!response.ok) {
      throw new Error(`AI key check failed: ${response.status}`);
    }
    return await response.json();
  }

  // ===========================================================================
  // Lifecycle (Trash/Restore)
  // ===========================================================================

  /**
   * Move config to trash (soft delete)
   */
  async trashConfig(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<ConfigResponse | null> {
    try {
      const params = new URLSearchParams({ docId });
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/trash?${params}`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to trash config: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[ConfigApi] trashConfig error:', error);
      return null;
    }
  }

  /**
   * Restore config from trash
   */
  async restoreConfig(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<ConfigResponse | null> {
    try {
      const params = new URLSearchParams({ docId });
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/restore?${params}`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to restore config: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[ConfigApi] restoreConfig error:', error);
      return null;
    }
  }

  /**
   * List trashed configs for a workspace
   */
  async listTrashed(
    workspaceId: string,
    olderThanDays?: number
  ): Promise<ConfigResponse[]> {
    try {
      const params = new URLSearchParams();
      if (olderThanDays !== undefined) {
        params.set('olderThanDays', String(olderThanDays));
      }

      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/trashed?${params}`
      );

      if (!response.ok) {
        throw new Error(`Failed to list trashed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[ConfigApi] listTrashed error:', error);
      return [];
    }
  }
}
